/**
 * idb.ts
 * Singleton partagé IndexedDB pour toute l'application.
 * Centralise l'ouverture, le versioning et le schéma de la DB.
 *
 * @version 1.0.0
 *
 * Utilisé par :
 *  - offlinePhotoStore.ts
 *  - useEdlDraftStore.ts
 *
 * ⚠️ NE PAS ouvrir la DB ailleurs — toujours passer par getDB() ici.
 */

const DB_NAME    = 'rentalcheck-offline'
const DB_VERSION = 3

let _dbPromise: Promise<IDBDatabase> | null = null

/**
 * Retourne la connexion IDB (singleton).
 * Crée les object stores si nécessaire (onupgradeneeded).
 */
export function getDB(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise

  _dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)

    req.onupgradeneeded = (e) => {
      const db      = (e.target as IDBOpenDBRequest).result
      const oldVer  = e.oldVersion   // 0 = première installation

      // ── v1 — Store photos ──────────────────────────────────────────────
      if (!db.objectStoreNames.contains('pending-photos')) {
        const store = db.createObjectStore('pending-photos', { keyPath: 'key' })
        // Index requis par offlinePhotoStore.getPhotos() / clearPhotos()
        store.createIndex('inspectionId', 'inspectionId', { unique: false })
      }

      // ── v2 — Store drafts ──────────────────────────────────────────────
      if (oldVer < 2 && !db.objectStoreNames.contains('edl-drafts')) {
        db.createObjectStore('edl-drafts', { keyPath: 'key' })
      }

      // ── v3 — Store departure-cache ─────────────────────────────────────
      // Cache persistant des photos de départ pour les retours hors connexion.
      // Clé = contractId, valeur = { contractId, photos, cachedAt }
      if (oldVer < 3 && !db.objectStoreNames.contains('departure-cache')) {
        db.createObjectStore('departure-cache', { keyPath: 'contractId' })
      }
    }

    req.onsuccess = () => {
      resolve(req.result)
    }

    req.onerror = () => {
      _dbPromise = null    // reset pour permettre un retry
      reject(new Error(`[IndexedDB] Ouverture échouée: ${req.error?.message}`))
    }

    req.onblocked = () => {
      console.warn('[IndexedDB] Connexion bloquée — fermer les autres onglets')
    }
  })

  return _dbPromise
}